import { useRef, useState } from 'react';
import { debounce } from 'underscore';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { StaticVideo } from '@/types/video.types';
import TextAreaAutosize from 'react-textarea-autosize';

type TSessionVodEmbedInfoProps = {
  sessionVideo: StaticVideo | null;
  isEditMode?: boolean;
  onReset?: () => void;
  onSave?: (embedCode: string, videoName: string) => void;
};

const SessionVodEmbedInfo = ({ sessionVideo, isEditMode = false, onReset, onSave }: TSessionVodEmbedInfoProps) => {
  const [embedCode, setEmbedCode] = useState(sessionVideo?.type === 'embed' ? sessionVideo?.source : '');

  const handleDebounce = useRef(
    debounce((value: string) => {
      if (value && onSave) {
        onSave(value, 'Embed');
      }
    }, 500)
  ).current;

  if (isEditMode) {
    return (
      <Container
        className="flex ai-center gap-12"
        height="84px"
        width="100%"
        background="var(--global-cont-1)"
        padding="12px"
        borderRadius="8px"
      >
        <Container className="flex-column ai-center jc-center gap-4" borderRadius="8px" height="84px" width="100%">
          <Container className="flex ai-center jc-space-between padding-h-8" width="100%">
            <Text className="L2" color="var(--gray-500)">
              Embed
            </Text>
            <Container className="flex ai-center gap-12">
              <Button padding="0" onClick={onReset}>
                <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                  Reset
                </Text>
              </Button>
              {/* TODO: implement add thumbnail
              <Text className='L2' color="var(--global-cont-3)">|</Text>
              <Button padding='0' onClick={() => {}}>
                <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                  Add thumbnail
                </Text>
              </Button> */}
            </Container>
          </Container>
          <TextAreaAutosize
            placeholder="Paste iframe"
            minRows={2}
            maxRows={2}
            style={{ border: '1px solid grey' }}
            value={embedCode || ''}
            autoCapitalize="none"
            className={'clear-textarea P2 radius-6 margin-b-6'}
            onChange={(e) => {
              setEmbedCode(e.target.value);
              handleDebounce(e.target.value);
            }}
          />
        </Container>
      </Container>
    );
  }

  if (!sessionVideo) return null;

  return (
    <Container className="flex ai-center jc-flex-start" borderRadius="8px" padding="12px" height="84px" width="100%">
      <Container className="flex ai-center jc-center gap-12" height="100%" width="100px">
        {sessionVideo.thumbnail_url && sessionVideo.thumbnail_url.trim() ? (
          <img src={sessionVideo.thumbnail_url} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px' }} />
        ) : (
          <Container
            height="100%"
            width="100%"
            borderRadius="8px"
            border="2px dashed var(--gray-600)"
            background="var(--gray-800)"
          />
        )}
      </Container>
      <Container className="flex-column" borderRadius="8px" padding="8px" width="100%">
        <Text className="S3" clamp={2} color="var(--gray-100)">
          {sessionVideo.source}
        </Text>
        <Text className="L2" color="var(--gray-300)">
          Embed
        </Text>
      </Container>
    </Container>
  );
};

export default SessionVodEmbedInfo;
